/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react';
import { Table, Spin } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { translate } from 'libs';

interface TableProps {
  columns: any[];
  dataSource: any[];
  query: Record<string, any>;
  loadDataAction: any;
  onRow?: (item, index) => any;
  className?: string;
  scroll: any;
  loadDatacallback?: () => void;
  isLoading?: boolean;
  expandable?: any;
  rowSelection?: any;
  dataType: string;
  onchangeSorter?: (sorter: any) => void;
  defaultSorter?:{
    field: string;
    order: 'ascend' | 'descend';
  }
}

const InfinityLoadTable = (props: TableProps) => {
  const {
    columns,
    dataSource,
    query,
    loadDataAction,
    onRow,
    className,
    scroll,
    loadDatacallback,
    expandable,
    rowSelection,
    dataType,
    // isLoading,
    onchangeSorter,
  } = props;

  const [page, setPage] = useState<number>(1);
  const hasNextPage = useRef<boolean>(true);
  const currentPage = useRef<number>(0);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const onLoadScroll = useCallback(() => {
    const tableContent: any = document.querySelector('.ant-table-body');
    if (tableContent) {
      tableContent.addEventListener('scroll', (event: any) => {
        const maxScroll: number = event.target.scrollHeight - event.target.clientHeight;
        const currentScroll: number = event.target.scrollTop;
        if (maxScroll - currentScroll <= 20 && maxScroll !== 0 && !loading && hasNextPage.current) {
          setPage(currentPage.current + 1);
        }
      });
    }
  }, [loading]);

  const onLoadSuccess = useCallback(() => {
    if (!loading) {
      const tableContent: any = document.querySelector('.ant-table-body');
      if (
        tableContent &&
        tableContent.scrollHeight === tableContent.clientHeight &&
        hasNextPage.current
      ) {
        setPage(currentPage.current + 1);
      }
    }
  }, [loading]);

  useEffect(() => {
    if (page > 1) {
      loadData(page);
    }
  }, [page]);

  useEffect(() => {
    onLoadScroll();
  }, []);

  useEffect(() => {
    const elementTable = document.querySelector('.ant-table-body');
    if (dataSource.length && elementTable && hasNextPage.current) {
      if (elementTable.scrollHeight === elementTable.clientHeight) {
        setPage(currentPage.current + 1);
      }
    }
  }, [dataSource.length, hasNextPage.current, currentPage.current]);

  useEffect(() => {
    if (query) {
      loadData(1);
      setPage(1);
    }
  }, [query]);

  const loadData = useCallback(
    (pageNumber = 1) => {
      const payload = {
        query: {
          page_number: pageNumber,
          page_size: 20,
          ...query,
        },
        onSuccess: (data: any) => {
          if (currentPage.current !== data?.pagination.page) {
            currentPage.current = data?.pagination.page;
          }
          hasNextPage.current = data?.pagination.hasNextPage;
          onLoadSuccess();
          setLoading(false);
          loadDatacallback && loadDatacallback();
        },
        onError: (errorCode: string) => {
          setLoading(false);
        },
      };
      setLoading(true);
      return dispatch(loadDataAction(payload));
    },
    [query]
  );

  const onChange = (pagination, filters, sorter, extra) => {
    onchangeSorter && onchangeSorter(sorter);
  };

  const isLoadingMore = loading && dataSource.length > 0 && page > 1;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [overlayBottom, setOverlayBottom] = useState(0);

  useLayoutEffect(() => {
    if (!isLoadingMore || !wrapperRef.current) return;
    const body = wrapperRef.current.querySelector(
      '.ant-table-body'
    ) as HTMLElement | null;
    if (!body) return;
    const wrapRect = wrapperRef.current.getBoundingClientRect();
    const bodyRect = body.getBoundingClientRect();
    setOverlayBottom(
      Math.max(0, wrapRect.bottom - (bodyRect.top + body.clientHeight))
    );
  }, [isLoadingMore]);

  const finalColumns = columns.map((col) => {
    if (props.defaultSorter && props.defaultSorter.field === col.dataIndex) {
      return {
        ...col,
        sortOrder: props.defaultSorter.order,
      };
    }
    return col;
  });

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <Table
        columns={finalColumns}
        dataSource={dataSource}
        expandable={expandable}
        rowSelection={rowSelection}
        className={className}
        onRow={onRow}
        scroll={scroll}
        loading={loading && (!dataSource.length || page === 1)}
        pagination={false}
        rowKey="_id"
        locale={{ emptyText: translate('no_data', { name: dataType }) }}
        onChange={onChange}
      />
      {isLoadingMore && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: overlayBottom + 12,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <span
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.92)',
              boxShadow: '0 1px 6px rgba(0, 0, 0, 0.16)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin size="small" />
          </span>
        </div>
      )}
    </div>
  );
};

export default React.memo(InfinityLoadTable);
